import {
  type AriaRole,
  type ChangeEvent,
  type ComponentPropsWithoutRef,
  type Ref,
  forwardRef,
} from "react";
import { t } from "ttag";

import { Button, Flex, Input } from "metabase/ui";

interface AddRowProps extends Omit<
  ComponentPropsWithoutRef<typeof Input>,
  "children" | "onChange" | "placeholder" | "value"
> {
  value: string;
  isValid: boolean;
  placeholder?: string;
  ariaLabel?: string;
  "aria-label"?: string;
  role?: AriaRole;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onDone: () => void;
  onCancel: () => void;
  children?: React.ReactNode;
}

export const AddRow = forwardRef(function AddRow(
  {
    value,
    isValid,
    placeholder,
    ariaLabel,
    "aria-label": ariaLabelFromProps,
    onKeyDown,
    onChange,
    onDone,
    onCancel,
    children,
    ...inputProps
  }: AddRowProps,
  ref: Ref<HTMLInputElement>,
) {
  return (
    <Flex
      my="1rem"
      p="0.5rem"
      display="relative"
      align="center"
      bd="1px solid var(--mb-color-core-brand)"
      style={{ borderRadius: "0.5rem" }}
    >
      {children}
      <Input
        {...inputProps}
        ref={ref}
        type="text"
        variant="unstyled"
        flex="1 0 auto"
        fz="lg"
        styles={{ input: { background: "transparent" } }}
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel ?? ariaLabelFromProps}
        autoFocus
        onKeyDown={onKeyDown}
        onChange={onChange}
      />
      <Button variant="subtle" bg="transparent" onClick={onCancel} mr="sm">
        {t`Cancel`}
      </Button>
      <Button
        variant={isValid ? "filled" : "outline"}
        disabled={!isValid}
        onClick={onDone}
      >
        {t`Add`}
      </Button>
    </Flex>
  );
});
